import { Formik } from 'formik'
import { keys, isEmpty } from 'lodash'
import { ReactElement, useState } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { useNavigate } from 'react-router-dom'

import { Input } from 'components/Input'
import { Label } from 'components/v2/Label'
import { SelectTags } from 'components/v2/SelectTags'
import { Tag } from 'components/v2/Tag'
import { DateRangeFilter } from 'features/App/components/Filters/DateRangeFilter'
import { changeTypeMessages } from 'features/Changes/components/Type/messages'
import { CompanyAutocomplete } from 'features/Companies/components/CompanyAutocomplete'
import { GTChangeType, GTCompany } from 'gql/types.generated'
import { dateRangeToString, getDateRangeQuery } from 'utils/date'
import { useLogin } from 'features/Login/hooks'
import { safeFormatMessage } from 'utils/intl'
import { getQueryString } from 'utils/route'
import { IChangeFiltersProps, IFilters } from './types'
import { IDateRangeFilter } from 'features/App/components/Filters/DateRangeFilter/types'
import {
  FilterHeader,
  FiltersContainer,
  FiltersSubmit,
  FiltersWrapper,
  FilterToggle,
  FilterWrapper,
  TagsWrapper
} from 'components/Filters'
import { UserAutocomplete } from 'features/Users/components/UserAutocomplete'
import { getUserLabel } from 'features/Users/utils'

const EXECUTED_CHANGE_TYPE_LIST = [
  GTChangeType.RouteServiceRouteChanged,
  GTChangeType.RouteServiceItineraryChanged,
  GTChangeType.TripCancelled,
  GTChangeType.RouteServiceDeactivated,
  GTChangeType.RouteServiceActivePeriodChanged,
  GTChangeType.RouteServiceVehicleChanged,
  GTChangeType.TripVehicleChanged,
  GTChangeType.TripItineraryChanged,
  GTChangeType.TripRouteChanged
]

const SCHEDULED_CHANGE_TYPE_LIST = [GTChangeType.ChangeRouteServicesRoute, GTChangeType.ChangeRouteServiceItinerary]

const messages = defineMessages({
  idPlaceholder: {
    id: 'changes.filters.idPlaceholder',
    defaultMessage: 'Id'
  },
  selectDateRange: {
    id: 'changes.filters.selectDateRange.placeholder',
    defaultMessage: 'Select date range'
  },
  serviceCodePlaceholder: {
    id: 'changes.filters.serviceCode.placeholder',
    defaultMessage: 'E.g: CR123456'
  },
  selectChangeType: {
    id: 'changes.filters.type.placeholder',
    defaultMessage: 'Select change type'
  },
  companyPlaceholder: {
    id: 'changes.filters.company.placeholder',
    defaultMessage: 'Select company'
  },
  selectUser: {
    id: 'changes.filters.selectUser',
    defaultMessage: 'Select User'
  }
})

export const ChangesFilters = ({
  filters,
  baseUrl = '/changes',
  isScheduledChanges,
  dateRangeDaysLimit
}: IChangeFiltersProps): ReactElement => {
  const navigate = useNavigate()
  const intl = useIntl()
  const [isShowingFilters, showFilters] = useState(false)
  const login = useLogin()
  const showCompany = login.isSuperAdmin

  const toggleFilters = (): void => {
    showFilters(!isShowingFilters)
  }

  const changeTypeList: GTChangeType[] = isScheduledChanges ? SCHEDULED_CHANGE_TYPE_LIST : EXECUTED_CHANGE_TYPE_LIST

  const getFilteredTypes = (values: GTChangeType[]): GTChangeType[] => {
    return changeTypeList
      .filter((type) => {
        return values.indexOf(type) === -1
      })
      .sort((a, b) => {
        const aTranslation = safeFormatMessage({ intl, messages: changeTypeMessages, key: a })
        const bTranslation = safeFormatMessage({ intl, messages: changeTypeMessages, key: b })
        return aTranslation.localeCompare(bTranslation)
      })
  }

  const filterChanges = (values: IFilters): void => {
    const dateRangeQuery = getDateRangeQuery(values.dateRange)
    const query = {
      id: values.id,
      dateRangeFrom: dateRangeQuery.from,
      dateRangeTo: dateRangeQuery.to,
      dateRangeValue: dateRangeQuery.rangeValue,
      serviceCode: values.serviceCode,
      types: values.types.join(','),
      companyId: values.company.id,
      userId: values.user.id
    }

    navigate(`${baseUrl}?${getQueryString(query)}`)
  }

  const defaultValues = {
    id: '',
    dateRange: {
      from: '',
      to: '',
      rangeValue: ''
    },
    serviceCode: '',
    types: [],
    company: { id: '', name: '' },
    user: { id: '', firstName: '', lastName: '', username: '' }
  }

  const initialValues = {
    id: filters.id || defaultValues.id,
    dateRange: filters.dateRange || defaultValues.dateRange,
    serviceCode: filters.serviceCode || defaultValues.serviceCode,
    types: filters.types || defaultValues.types,
    company: filters.company || defaultValues.company,
    user: filters.user || defaultValues.user
  }

  return (
    <Formik initialValues={initialValues} onSubmit={filterChanges} enableReinitialize>
      {(props): ReactElement => {
        const { handleChange, handleSubmit, setFieldValue, values } = props

        const handleFilterDelete = (id: keyof IFilters): void => {
          setFieldValue(id, defaultValues[id])
          filterChanges({
            ...values,
            [id]: defaultValues[id]
          })
        }

        const handleTypesChange = (types: GTChangeType[]): void => {
          setFieldValue('types', types)
          filterChanges({
            ...values,
            types
          })
        }

        return (
          <form onSubmit={handleSubmit}>
            <div>
              <FilterHeader>
                <FilterWrapper>
                  <div>
                    <FilterToggle onClick={toggleFilters} />
                  </div>
                  <TagsWrapper>
                    {keys(filters).map((filterKey): TSFixMe => {
                      let label, value
                      const filterValue = filters[filterKey as keyof typeof filters]
                      const hasValue = !isEmpty(filterValue)

                      if (!hasValue) return null

                      switch (filterKey) {
                        case 'id':
                          label = <FormattedMessage id="dictionary.id" defaultMessage="Id" />
                          value = filterValue
                          break
                        case 'serviceCode':
                          label = <FormattedMessage id="change.filters.serviceCode.tag" defaultMessage="Service code" />
                          value = filterValue
                          break
                        case 'types':
                          label = <FormattedMessage id="change.filters.types.tag" defaultMessage="Types" />
                          value = (filterValue as GTChangeType[])
                            .map((type): TSFixMe => {
                              return safeFormatMessage({ intl, messages: changeTypeMessages, key: type })
                            })
                            .join(', ')
                          break
                        case 'dateRange':
                          label = <FormattedMessage id="changes.filters.dateRange.tag" defaultMessage="Date range" />
                          value = dateRangeToString(intl, filterValue as IDateRangeFilter)

                          if (!value) return null

                          break
                        case 'company':
                          if (!(filterValue as Pick<GTCompany, 'id' | 'name'>).id) {
                            return null
                          }
                          label = <FormattedMessage id="changes.filters.company.tag" defaultMessage="company" />
                          value = (filterValue as Pick<GTCompany, 'id' | 'name'>).name

                          break
                        case 'user':
                          if (!(filterValue as IFilters['user']).id) {
                            return null
                          }
                          label = <FormattedMessage id="changes.filters.user.tag" defaultMessage="User" />
                          value = getUserLabel(filterValue as IFilters['user'])
                          break
                      }

                      return (
                        <Tag key={filterKey} onDelete={handleFilterDelete.bind(this, filterKey as keyof IFilters)}>
                          {label}: {value as string}
                        </Tag>
                      )
                    })}
                  </TagsWrapper>
                </FilterWrapper>
              </FilterHeader>
              <FiltersWrapper isShowingFilters={isShowingFilters}>
                <FiltersContainer>
                  <Input
                    id="id"
                    value={values.id}
                    onChange={handleChange}
                    placeholder={intl.formatMessage(messages.idPlaceholder)}
                    label={<FormattedMessage id="dictionary.id" defaultMessage="Id" />}
                  />
                  <UserAutocomplete
                    onChange={(value): void => {
                      setFieldValue('user', value)
                      filterChanges({
                        ...values,
                        user: value
                      })
                    }}
                    value={getUserLabel(values.user)}
                    placeholder={intl.formatMessage(messages.selectUser)}
                    label={<FormattedMessage id="changes.filters.user.label" defaultMessage="User" />}
                  />
                  <SelectTags
                    id="types"
                    placeholder={intl.formatMessage(messages.selectChangeType)}
                    values={values.types}
                    items={getFilteredTypes(values.types)}
                    getTagLabel={(type: GTChangeType): string => {
                      return safeFormatMessage({ intl, messages: changeTypeMessages, key: type })
                    }}
                    onChange={handleTypesChange}
                    label={<FormattedMessage id="changes.filters.types.label" defaultMessage="Types" />}
                  />
                  <Input
                    id="serviceCode"
                    value={values.serviceCode}
                    onChange={handleChange}
                    placeholder={intl.formatMessage(messages.serviceCodePlaceholder)}
                    label={<FormattedMessage id="changes.filters.serviceCode.label" defaultMessage="Service code" />}
                  />
                  <div>
                    <Label>
                      <FormattedMessage id="changes.filters.dateRange.label" defaultMessage="Date range" />
                    </Label>
                    <DateRangeFilter
                      value={values.dateRange}
                      placeholder={intl.formatMessage(messages.selectDateRange)}
                      onChange={(value): void => {
                        setFieldValue('dateRange', value)

                        filterChanges({
                          ...values,
                          dateRange: value
                        })
                      }}
                      dateRangeDaysLimit={dateRangeDaysLimit}
                    />
                  </div>
                  {showCompany && (
                    <CompanyAutocomplete
                      id="company.id"
                      onChange={(value: Pick<GTCompany, 'id' | 'name'>): void => {
                        setFieldValue('company', value)
                        filterChanges({
                          ...values,
                          company: value
                        })
                      }}
                      value={values.company.name}
                      placeholder={intl.formatMessage(messages.companyPlaceholder)}
                      label={<FormattedMessage id="dictionary.company" defaultMessage="Company" />}
                    />
                  )}
                </FiltersContainer>
              </FiltersWrapper>
            </div>
            <FiltersSubmit />
          </form>
        )
      }}
    </Formik>
  )
}
